import { v4 as uuid } from 'uuid';

import {
    ServiceGroupCreateForm,
    ServiceGroupServiceData,
    ServiceGroupServiceUpdateForm,
    ServiceGroupAddServiceParams,
    Optional,
    Nullable,
} from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { serviceService } from '@services/services/serviceService';
import { ServiceGroupApi } from '@api/ServiceGroupApi';
import { ValidationParams } from '@components/types';
import { translate } from '@chaika/i18n/helper';

import { ServiceGroupServiceFilterParams } from './ServiceGroupServiceList';

class ServiceGroupService {
    public async load(id: string) {
        const serviceGroupPage = store.serviceGroupPage;

        if (id !== 'new') {
            const { limit } = serviceGroupPage.services;

            const [serviceGroup, { services, count }] = await Promise.all([
                ServiceGroupApi.getServiceGroup(id),
                ServiceGroupApi.getServiceList(id, { limit }),
            ]);

            const servicesIds = services.map(service => service.serviceId);

            await serviceService.loadByIds(servicesIds);

            serviceGroupPage.id = id;
            serviceGroupPage.name = serviceGroup.name;
            serviceGroupPage.services.set(services);
            serviceGroupPage.services.setFilterParams({});
            serviceGroupPage.services.count = count;
            serviceGroupPage.createNewMode = false;
        } else {
            serviceGroupPage.id = uuid();
            serviceGroupPage.name = '';
            serviceGroupPage.createNewMode = true;
        }
    }

    public async loadNext() {
        const servicesList = store.serviceGroupPage.services;

        if (servicesList.hasNext) {
            const { id } = store.serviceGroupPage;

            const params = {
                ...servicesList.getFilterParams(),
                ...servicesList.queryParams,
            };

            const { services } = await ServiceGroupApi.getServiceList(id, params);

            const servicesIds = services.map(service => service.serviceId);
            await serviceService.loadByIds(servicesIds);

            servicesList.addNext(services);
        }
    }

    public async reload(filterParams: ServiceGroupServiceFilterParams) {
        const servicesList = store.serviceGroupPage.services;
        const { id } = store.serviceGroupPage;

        servicesList.reset();

        const { services, count } = await ServiceGroupApi.getServiceList(id, {
            ...filterParams,
            ...servicesList.queryParams,
        });

        const servicesIds = services.map(service => service.serviceId);
        await serviceService.loadByIds(servicesIds);

        servicesList.set(services);
        servicesList.setFilterParams(filterParams);
        servicesList.count = count;
    }

    public async updateName(name: string) {
        const serviceGroupPage = store.serviceGroupPage;

        if (!serviceGroupPage.createNewMode) {
            await ServiceGroupApi.update(serviceGroupPage.id, { name });
        }

        serviceGroupPage.name = name;
    }

    public async addService(params: ServiceGroupAddServiceParams) {
        const serviceGroupPage = store.serviceGroupPage;

        if (!serviceGroupPage.createNewMode) {
            await ServiceGroupApi.addService(serviceGroupPage.id, {
                // @ts-ignore
                startDate: new Date().toISOString(),
                ...params,
            });
        }

        await serviceService.loadByIds([params.serviceId]);

        serviceGroupPage.services.add([params as ServiceGroupServiceData]);
        serviceGroupPage.services.count++;
    }

    public validateCreateParams({ serviceId }: Partial<ServiceGroupAddServiceParams>): ValidationParams {
        return {
            isValid: !!serviceId,
            message: translate('serviceMustSpecified'),
        };
    }

    public async updateService(serviceId: string, params: ServiceGroupServiceUpdateForm) {
        const serviceGroupPage = store.serviceGroupPage;

        if (!serviceGroupPage.createNewMode) {
            await ServiceGroupApi.updateService(serviceGroupPage.id, serviceId, params);
        }

        serviceGroupPage.services.update(serviceId, params);
    }

    public async createNew(): Promise<string> {
        const serviceGroupPage = store.serviceGroupPage;

        if (serviceGroupPage.createNewMode) {
            const params: ServiceGroupCreateForm = {
                id: serviceGroupPage.id,
                name: serviceGroupPage.name,
                services: serviceGroupPage.services.values.map(service => ({
                    serviceId: service.serviceId,
                    startDate: this.makeDate(service.startDate),
                    endDate: this.makeDate(service.endDate),
                })),
            };

            await ServiceGroupApi.create(params);

            serviceGroupPage.createNewMode = false;
        }

        return serviceGroupPage.id;
    }

    protected makeDate(dateString?: Nullable<string>): Optional<string> {
        return dateString?.split('T')[0];
    }
}

export const serviceGroupService = new ServiceGroupService();
